import React, { useEffect, useRef } from 'react';

interface LatLng {
  lat: number;
  lng: number;
}

interface DeliveryZoneMapProps {
  initialPolygon?: LatLng[] | null;
  centerCoordinates?: LatLng;
  onPolygonChange: (polygon: LatLng[] | null) => void;
  disabled?: boolean;
}

declare global {
  interface Window {
    google: any;
  }
}

export const DeliveryZoneMap: React.FC<DeliveryZoneMapProps> = ({
  initialPolygon,
  centerCoordinates,
  onPolygonChange,
  disabled = false,
}) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const drawingManagerRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);

  useEffect(() => {
    let scriptEl: HTMLScriptElement | null = null;

    const initMap = () => {
      if (!mapRef.current || !window.google?.maps) return;

      const center = centerCoordinates || { lat: 45.8992, lng: 6.1294 };

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 14,
        disableDefaultUI: false,
      });

      const hasInitialPolygon = initialPolygon && initialPolygon.length >= 3;

      if (hasInitialPolygon) {
        const poly = new window.google.maps.Polygon({
          paths: initialPolygon,
          editable: !disabled,
          draggable: !disabled,
        });
        poly.setMap(mapInstanceRef.current);
        polygonRef.current = poly;

        const bounds = new window.google.maps.LatLngBounds();
        initialPolygon.forEach((p) => bounds.extend(p));
        mapInstanceRef.current.fitBounds(bounds);

        attachPolygonListeners(poly);
      }

      if (!disabled) {
        const drawingManager = new window.google.maps.drawing.DrawingManager({
          drawingMode: hasInitialPolygon ? null : window.google.maps.drawing.OverlayType.POLYGON,
          drawingControl: !hasInitialPolygon,
          drawingControlOptions: {
            position: window.google.maps.ControlPosition.TOP_CENTER,
            drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
          },
          polygonOptions: {
            editable: true,
            draggable: true,
          },
        });

        drawingManager.setMap(mapInstanceRef.current);
        drawingManagerRef.current = drawingManager;

        window.google.maps.event.addListener(
          drawingManager,
          'overlaycomplete',
          (event: any) => {
            if (event.type === window.google.maps.drawing.OverlayType.POLYGON) {
              if (polygonRef.current) {
                polygonRef.current.setMap(null);
              }
              polygonRef.current = event.overlay;
              drawingManager.setDrawingMode(null);
              drawingManager.setOptions({ drawingControl: false });
              attachPolygonListeners(polygonRef.current);
              emitPolygonChange();
            }
          }
        );
      }
    };

    const attachPolygonListeners = (polygon: any) => {
      const paths = polygon.getPath();
      const update = () => emitPolygonChange();

      window.google.maps.event.addListener(paths, 'set_at', update);
      window.google.maps.event.addListener(paths, 'insert_at', update);
      window.google.maps.event.addListener(paths, 'remove_at', update);
    };

    const emitPolygonChange = () => {
      if (!polygonRef.current) {
        onPolygonChange(null);
        return;
      }
      const path = polygonRef.current.getPath();
      const vertices: LatLng[] = [];
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        vertices.push({ lat: point.lat(), lng: point.lng() });
      }
      if (vertices.length < 3) {
        onPolygonChange(null);
      } else {
        onPolygonChange(vertices);
      }
    };

    const loadScriptAndInit = () => {
      if (window.google?.maps) {
        initMap();
        return;
      }

      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        // eslint-disable-next-line no-console
        console.warn('VITE_GOOGLE_MAPS_API_KEY is not set; DeliveryZoneMap will not render a map.');
        return;
      }

      const existing = document.getElementById('google-maps-script') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', initMap);
        return;
      }

      scriptEl = document.createElement('script');
      scriptEl.id = 'google-maps-script';
      scriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=drawing`;
      scriptEl.async = true;
      scriptEl.defer = true;
      scriptEl.addEventListener('load', initMap);
      document.body.appendChild(scriptEl);
    };

    loadScriptAndInit();

    return () => {
      if (scriptEl) {
        scriptEl.removeEventListener('load', initMap);
      }
    };
  }, [initialPolygon, centerCoordinates, disabled, onPolygonChange]);

  const handleClear = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setOptions({
        drawingMode: window.google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
      });
    }
    onPolygonChange(null);
  };

  return (
    <div className="space-y-2">
      <div ref={mapRef} className="w-full h-80 rounded-md border border-gray-200" />
      {!disabled && (
        <button
          type="button"
          onClick={handleClear}
          className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Clear delivery zone
        </button>
      )}
    </div>
  );
};


